// Added by Sejune Oh on 2019-06-25
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, createRef } from 'react'
import { observable, action, computed, makeObservable } from 'mobx'
import { observer, inject } from 'mobx-react'
import copyToClipboard from 'copy-to-clipboard'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import Intl from '@/helper/Intl'
import { ReactEmptyEvent } from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import UserControl from './UserControl'
import { UserBaseCProps } from '../../../UserBase'
import Dialog from '@/components/common/Dialog'
import { UserStore } from '@/stores/mgmt/user'
import { UserAuthStore } from '@/stores/user/UserAuth'
import { CollapsibleForm } from '@/components/common/CollapsibleForm'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface UserControlCProps extends UserBaseCProps {
  user?: UserStore
  userAuth?: UserAuthStore
  onRemoveClick: ReactEmptyEvent
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('user', 'userAuth')
@observer
export default class UserControlContainer extends Component<UserControlCProps, {}> {
  mounted: boolean = false
  handleDialogConfirm?: () => Promise<{}>
  handleDialogCancel?: () => Promise<{}>

  _dialogRef = createRef<Dialog>()

  constructor(props: UserControlCProps) {
    super(props)
    makeObservable(this, {
      mounted: observable,
      handleDialogConfirm: observable,
      handleDialogCancel: observable,
      componentDidMount: action,
      _handleResetPwd: action,
      _handleToggleActive: action,
      _changeActivateStatus: action,
      userAuth: computed,
    })
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  componentDidMount = () => {
    this.mounted = true
    this.props.onChildComponentMount('userCtr')
  }

  render() {
    if (!this.props.userId) return null
    return (
      <CollapsibleForm>
        <UserControl
          auth2_enabled={this.props.userElement!.auth2_enabled!}
          activated={this.props.userElement!.activated!}
          onResetPwdBtnClick={this._handleResetPwd}
          onToggleActiveBtnClick={this._handleToggleActive}
          onToggleAuth2BtnClick={this._handleToggleAuth2}
          onResetAuth2BtnClick={this._handleResetAuth2}
          onRemoveClick={this.props.onRemoveClick}
          dialogRef={this._dialogRef}
          onDialogCancel={this.handleDialogCancel!}
          onDialogConfirm={this.handleDialogConfirm!}
        />
      </CollapsibleForm>
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  get userAuth() {
    return this.props.userAuth!
  }

  _handleResetPwd = () => {
    this.handleDialogConfirm = () => {
      return new Promise(async (resolve) => {
        const password = await this.props.user!.resetPassword(this.props!.userId!)
        const message = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.noti_reset_pwd_done' })
        this.props.showSnackbar!('info', message)
        copyToClipboard(password)
        resolve(true)
      })
    }

    const title = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.dialog_title_reset_password' })
    const description = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.dialog_desc_reset_password' })
    this._dialogRef.current!.setDialogInfo({ title, description })

    this._dialogRef.current!.handleOpen()
  }

  _handleToggleActive = () => {
    // System admin계정의 deactivation을 방지
    if (this.props.userElement!.id === '1') {
      // prettier-ignore
      const message = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.noti_can_not_deactivate_sys_admin' })
      this.props.showSnackbar!('err', message)
      return
    }

    if (this.userAuth.userInfo!.username === this.props.userElement!.username) {
      // prettier-ignore
      const message = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.noti_can_not_remove_current' })
      this.props.showSnackbar!('err', message)
      return
    }

    let title = ''
    let description = ''
    let resMessage = ''

    if (this.props.userElement!.activated!) {
      title = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.dialog_title_disable_user' })
      description = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.dialog_desc_disable_user' })
      resMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.noti_user_disabled' })
    } else {
      title = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.dialog_title_enable_user' })
      description = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.dialog_desc_enable_user' })
      resMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.noti_user_enabled' })
    }

    this.handleDialogConfirm = () => {
      return new Promise((resolve) => {
        this._changeActivateStatus()
        this.props.showSnackbar!('info', resMessage)
        resolve(true)
      })
    }

    this._dialogRef.current!.setDialogInfo({
      description,
      title,
    })

    this._dialogRef.current!.handleOpen()
  }

  _handleToggleAuth2 = () => {
    // System admin계정의 deactivation을 방지
    if (this.props.userElement!.id === '1') {
      // prettier-ignore
      const message = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.noti_can_not_disabled_2fa_sys_admin' })
      this.props.showSnackbar!('err', message)
      return
    }

    let title = ''
    let description = ''
    let resMessage = ''

    if (this.props.userElement!.auth2_enabled!) {
      title = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.dialog_title_disable_2fa' })
      description = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.dialog_desc_disable_2fa' })
      resMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.noti_2fa_disabled' })
    } else {
      title = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.dialog_title_enable_2fa' })
      description = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.dialog_desc_enable_2fa' })
      resMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.noti_2fa_enabled' })
    }

    this.handleDialogConfirm = () => {
      return new Promise((resolve) => {
        this._changeAuth2Status()
        this.props.showSnackbar!('info', resMessage)
        resolve(true)
      })
    }

    this._dialogRef.current!.setDialogInfo({
      description,
      title,
    })

    this._dialogRef.current!.handleOpen()
  }

  _handleResetAuth2 = () => {
    // System admin계정의 deactivation을 방지
    if (this.props.userElement!.id === '1') {
      // prettier-ignore
      const message = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.noti_can_not_reset_2fa_sys_admin' })
      this.props.showSnackbar!('err', message)
      return
    }

    let title = ''
    let description = ''
    let resMessage = ''

    title = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.dialog_title_reset_2fa' })
    description = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.dialog_desc_reset_2fa' })
    resMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.usercontrolcontainer.noti_reset_2fa_done' })

    this.handleDialogConfirm = () => {
      return new Promise((resolve) => {
        this._resetAuth2Data()
        this.props.showSnackbar!('info', resMessage)
        resolve(true)
      })
    }

    this._dialogRef.current!.setDialogInfo({
      description,
      title,
    })

    this._dialogRef.current!.handleOpen()
  }

  _changeActivateStatus = async () => {
    await this.props.user!.updateElement(this.props!.userId!, {
      ...this.props.userElement!,
      activated: !this.props.userElement!.activated!,
    })

    this.props.userElement!.activated! = !this.props.userElement!.activated!
  }

  _changeAuth2Status = async () => {
    await this.props.user!.updateElement(this.props!.userId!, {
      ...this.props.userElement!,
      auth2_enabled: !this.props.userElement!.auth2_enabled!,
    })
    this.props.userElement!.auth2_enabled! = !this.props.userElement!.auth2_enabled!
  }

  _resetAuth2Data = async () => {
    await this.props.user!.resetAuth2Data(this.props!.userId!)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getActivationStatus = () => {
    return this.props.userElement!.activated!
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getAuth2Status = () => {
    return this.props.userElement!.auth2_enabled!
  }
}

